<template>
  <div class="condition-config">
    <div v-if="!triggerType" class="alert alert--info">
      Configure a trigger before setting up a condition.
    </div>

    <template v-else>
      <div v-if="triggerChangedWarning" class="alert alert--warning">
        The trigger changed and the previously selected field is no longer
        available. Please pick a new field.
      </div>

      <div v-if="!availableFields.length" class="alert alert--info">
        No filterable fields available for this trigger yet.
      </div>

      <template v-else>
        <SelectInput
          id="condition-field"
          label="Field"
          :options="fieldOptions"
          :model-value="localCondition.field"
          @update:model-value="onFieldChange"
        />

        <SelectInput
          id="condition-operator"
          label="Operator"
          :options="operatorOptions"
          :model-value="localCondition.operator"
          @update:model-value="onOperatorChange"
        />

        <template v-if="requiresValue">
          <SelectInput
            v-if="selectedFieldOptions.length"
            id="condition-value-select"
            label="Value"
            :options="selectedFieldOptions"
            :model-value="localCondition.value"
            @update:model-value="onValueChange"
          />
          <TextInput
            v-else
            id="condition-value"
            label="Value"
            :type="isNumericField ? 'number' : 'text'"
            :model-value="localCondition.value"
            :placeholder="valuePlaceholder"
            :error="valueError"
            @update:model-value="onValueChange"
          />
        </template>

        <p v-if="showFormFieldsHint" class="condition-config__hint">
          Form fields are prefixed with <code>submission.data.</code> so that the
          backend can resolve them.
        </p>
      </template>
    </template>
  </div>
</template>

<script>
  import { ref, computed, watch } from 'vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import { useFormStore } from '@/store/formStore';
  import { useDealStore } from '@/store/dealStore';
  import { dealService } from '@/services/dealService';
  import {
    FIELD_TYPES,
    TRIGGER_FIELD_CATALOG,
    OPERATOR_CATALOG,
    getOperatorsForFieldType,
    operatorRequiresValue,
    isNumericOperator,
    normalizeOperator,
  } from '@/constants/automationFieldCatalog';

  const FORM_FIELD_PREFIX = 'submission.data.';
  const STAGE_FIELD_PATHS = ['stage.id', 'from_stage.id', 'to_stage.id', 'deal.stage_id'];

  const FORM_ELEMENT_TYPE_TO_FIELD_TYPE = {
    text: FIELD_TYPES.STRING,
    email: FIELD_TYPES.STRING,
    phone: FIELD_TYPES.STRING,
    textarea: FIELD_TYPES.STRING,
    number: FIELD_TYPES.NUMBER,
    select: FIELD_TYPES.ENUM,
    radio: FIELD_TYPES.ENUM,
    checkbox: FIELD_TYPES.ENUM,
    date: FIELD_TYPES.DATE,
  };

  function migrateConditionConfig(raw) {
    if (!raw || typeof raw !== 'object') {
      return { condition: { field: '', operator: 'equals', value: '' } };
    }

    // Legacy { conditions: [...] } → first item
    if (Array.isArray(raw.conditions) && raw.conditions.length) {
      const c = raw.conditions[0] || {};
      return {
        ...raw,
        condition: {
          field: c.field || '',
          operator: normalizeOperator(c.operator || 'equals'),
          value: c.value ?? '',
        },
      };
    }

    // Already new shape
    if (raw.condition && typeof raw.condition === 'object') {
      return {
        ...raw,
        condition: {
          field: raw.condition.field || '',
          operator: normalizeOperator(raw.condition.operator || 'equals'),
          value: raw.condition.value ?? '',
        },
      };
    }

    // Flat legacy shape
    if (raw.field || raw.operator) {
      return {
        condition: {
          field: raw.field || '',
          operator: normalizeOperator(raw.operator || 'equals'),
          value: raw.value ?? '',
        },
      };
    }

    return { condition: { field: '', operator: 'equals', value: '' } };
  }

  export default {
    name: 'ConditionConfig',

    components: { SelectInput, TextInput },

    props: {
      modelValue: {
        type: Object,
        required: true,
      },
      currentAutomation: {
        type: Object,
        required: true,
      },
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {
      const formStore = useFormStore();
      const dealStore = useDealStore();

      const migrated = migrateConditionConfig(props.modelValue);
      const localConfig = ref(migrated);
      const localCondition = ref({ ...migrated.condition });
      const triggerChangedWarning = ref(false);

      const triggerType = computed(
        () => props.currentAutomation?.trigger_type || null
      );

      const triggerDetails = computed(
        () => props.currentAutomation?.trigger_details || {}
      );

      // Form-submission specific: append dynamic form element fields. The
      // /forms list endpoint no longer ships the full pages/elements blob, so
      // fetch the full form on demand via formStore.ensureFormDetails (cached).
      const currentFormId = computed(() => {
        if (triggerType.value !== 'form_submission') return null;
        return triggerDetails.value?.formId || props.currentAutomation?.form_id || null;
      });

      const currentForm = ref(null);
      const stageValueOptions = ref([]);

      watch(
        currentFormId,
        async (formId) => {
          if (!formId) {
            currentForm.value = null;
            return;
          }
          // Show cached value immediately if we have it; otherwise wait for fetch.
          const cached = formStore.formDetailsCache[formId];
          if (cached) {
            currentForm.value = cached;
          }
          try {
            const form = await formStore.ensureFormDetails(formId);
            if (formId === currentFormId.value) {
              currentForm.value = form || null;
            }
          } catch (error) {
            console.error('Failed to load form for condition config:', error);
            if (formId === currentFormId.value) currentForm.value = null;
          }
        },
        { immediate: true }
      );

      const dynamicFormFields = computed(() => {
        const form = currentForm.value;
        if (!Array.isArray(form?.pages)) return [];

        const fields = [];
        const addElement = (el) => {
          if (!el?.id) return;
          const type =
            FORM_ELEMENT_TYPE_TO_FIELD_TYPE[el.type] || FIELD_TYPES.STRING;
          const label = el.props?.label || el.props?.placeholder || el.id;
          const options =
            type === FIELD_TYPES.ENUM && Array.isArray(el.props?.options)
              ? el.props.options
                  .map((o) =>
                    typeof o === 'string'
                      ? { value: o, label: o }
                      : { value: o.value ?? o.label, label: o.label ?? o.value }
                  )
                  .filter((o) => o.value !== undefined && o.value !== null)
              : null;
          fields.push({
            path: `${FORM_FIELD_PREFIX}${el.id}`,
            label: `📝 ${label}`,
            type,
            options,
          });
        };
        form.pages.forEach((page) => {
          // Forms store elements directly on the page (page.elements). Older
          // shapes nest them under page.blocks[].elements; support both.
          (page.elements || []).forEach(addElement);
          (page.blocks || []).forEach((block) =>
            (block.elements || []).forEach(addElement)
          );
        });
        return fields;
      });

      const availableFields = computed(() => {
        const base = TRIGGER_FIELD_CATALOG[triggerType.value] || [];
        return [...base, ...dynamicFormFields.value];
      });

      const selectedFieldPath = computed(() => localCondition.value.field || '');
      const isStageFieldSelected = computed(() =>
        STAGE_FIELD_PATHS.includes(selectedFieldPath.value)
      );

      const triggerPipelineId = computed(
        () => triggerDetails.value?.pipelineId || null
      );

      const fieldOptions = computed(() => {
        const opts = availableFields.value.map((f) => ({
          value: f.path,
          label: f.label,
        }));
        // Always provide an empty option so the user has to pick.
        return [{ value: '', label: '— Select a field —' }, ...opts];
      });

      const selectedField = computed(() =>
        availableFields.value.find((f) => f.path === localCondition.value.field) ||
        null
      );

      const operatorOptions = computed(() => {
        const fieldType = selectedField.value?.type;
        return getOperatorsForFieldType(fieldType).map((op) => ({
          value: op.value,
          label: op.label,
        }));
      });

      const requiresValue = computed(() =>
        operatorRequiresValue(localCondition.value.operator)
      );

      const isNumericField = computed(
        () =>
          selectedField.value?.type === FIELD_TYPES.NUMBER ||
          isNumericOperator(localCondition.value.operator)
      );

      const selectedFieldOptions = computed(() => {
        if (isStageFieldSelected.value && stageValueOptions.value.length) {
          return [{ value: '', label: '— Select a stage —' }, ...stageValueOptions.value];
        }
        const field = selectedField.value;
        if (!field || !Array.isArray(field.options) || !field.options.length)
          return [];
        const opts = field.options.map((o) =>
          typeof o === 'string' ? { value: o, label: o } : o
        );
        return [{ value: '', label: '— Select a value —' }, ...opts];
      });

      const valuePlaceholder = computed(() => {
        if (isNumericField.value) return 'Enter a number';
        if (selectedField.value?.type === FIELD_TYPES.DATE) return 'YYYY-MM-DD';
        return 'Enter value to compare';
      });

      const valueError = computed(() => {
        if (!requiresValue.value) return '';
        const v = localCondition.value.value;
        if (v === '' || v === null || v === undefined) return '';
        if (isNumericField.value && Number.isNaN(Number(v))) {
          return 'Value must be numeric';
        }
        return '';
      });

      const showFormFieldsHint = computed(
        () => !!dynamicFormFields.value.length
      );

      const emitUpdate = () => {
        const next = {
          ...localConfig.value,
          condition: { ...localCondition.value },
        };
        localConfig.value = next;
        emit('update:modelValue', next);
      };

      const mapStagesToOptions = (stages = []) =>
        stages
          .filter((s) => s?.id)
          .map((s) => ({
            value: s.id,
            label: s.name || String(s.id),
          }));

      const dedupeOptionsByValue = (options) => {
        const byValue = new Map();
        options.forEach((opt) => {
          if (!byValue.has(opt.value)) byValue.set(opt.value, opt);
        });
        return Array.from(byValue.values());
      };

      const loadStageOptions = async () => {
        if (!isStageFieldSelected.value) {
          stageValueOptions.value = [];
          return;
        }

        try {
          if (!dealStore.pipelines.length) {
            await dealStore.fetchPipelines();
          }

          if (triggerPipelineId.value) {
            const { stages } = await dealService.getPipelineStages(triggerPipelineId.value);
            stageValueOptions.value = mapStagesToOptions(stages || []);
            return;
          }

          const pipelineIds = dealStore.pipelines.map((p) => p.id).filter(Boolean);
          if (!pipelineIds.length) {
            stageValueOptions.value = [];
            return;
          }

          const stageLists = await Promise.all(
            pipelineIds.map(async (pipelineId) => {
              try {
                const { stages } = await dealService.getPipelineStages(pipelineId);
                return mapStagesToOptions(stages || []);
              } catch {
                return [];
              }
            })
          );

          stageValueOptions.value = dedupeOptionsByValue(stageLists.flat());
        } catch (error) {
          console.error('Failed to load stage options for condition field:', error);
          stageValueOptions.value = [];
        }
      };

      const onFieldChange = (value) => {
        triggerChangedWarning.value = false;
        localCondition.value.field = value;

        // If current operator isn't supported by the new field type, reset.
        const newFieldType = availableFields.value.find((f) => f.path === value)
          ?.type;
        const supported = getOperatorsForFieldType(newFieldType).map(
          (o) => o.value
        );
        if (!supported.includes(localCondition.value.operator)) {
          localCondition.value.operator = supported[0] || 'equals';
        }

        // Clear value when switching field (enum options differ).
        localCondition.value.value = '';
        emitUpdate();
      };

      const onOperatorChange = (value) => {
        localCondition.value.operator = value;
        if (!operatorRequiresValue(value)) {
          localCondition.value.value = '';
        }
        emitUpdate();
      };

      const onValueChange = (value) => {
        localCondition.value.value = value;
        emitUpdate();
      };

      // External changes (e.g. migration after trigger switch).
      watch(
        () => props.modelValue,
        (newVal) => {
          const m = migrateConditionConfig(newVal);
          if (
            JSON.stringify(m.condition) !== JSON.stringify(localCondition.value)
          ) {
            localConfig.value = m;
            localCondition.value = { ...m.condition };
          }
        },
        { deep: true }
      );

      watch(
        [selectedFieldPath, triggerPipelineId],
        () => {
          loadStageOptions();
        },
        { immediate: true }
      );

      // When trigger type changes and current field isn't valid anymore,
      // reset silently and surface a warning banner.
      watch(triggerType, () => {
        const fieldPaths = availableFields.value.map((f) => f.path);
        if (
          localCondition.value.field &&
          !fieldPaths.includes(localCondition.value.field)
        ) {
          localCondition.value.field = '';
          localCondition.value.value = '';
          triggerChangedWarning.value = true;
          emitUpdate();
        } else {
          triggerChangedWarning.value = false;
        }
      });

      return {
        triggerType,
        triggerChangedWarning,
        availableFields,
        fieldOptions,
        operatorOptions,
        selectedFieldOptions,
        requiresValue,
        isNumericField,
        valuePlaceholder,
        valueError,
        showFormFieldsHint,
        localCondition,
        onFieldChange,
        onOperatorChange,
        onValueChange,
      };
    },
  };
</script>

<style scoped lang="scss">
  .condition-config {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .alert {
      font-style: italic;
      font-size: 0.875rem;
      padding: 0.75rem;
      border-radius: 6px;

      &--info {
        background: var(--blue-50);
        border: 1px solid var(--blue-200);
        color: var(--blue-700);
      }

      &--warning {
        background: var(--amber-50, #fffbeb);
        border: 1px solid var(--amber-200, #fde68a);
        color: var(--amber-700, #b45309);
      }
    }

    &__hint {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin: 0;

      code {
        font-family: monospace;
        background: var(--slate-100);
        padding: 0 3px;
        border-radius: 3px;
      }
    }
  }
</style>
